import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
// ---------- Foods ----------
export const useFoods = () => useQuery({
    queryKey: ['foods'],
    queryFn: async () => (await api.get('/foods')).data,
});
export const useCreateFood = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/foods', input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['foods'] }),
    });
};
export const useUpdateFood = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...input }) => (await api.put(`/foods/${id}`, input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['foods'] }),
    });
};
export const useDeleteFood = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`/foods/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['foods'] }),
    });
};
// ---------- Groups ----------
export const useGroups = (companyId) => useQuery({
    queryKey: ['groups', companyId ?? 'all'],
    queryFn: async () => (await api.get('/groups', { params: companyId ? { companyId } : {} })).data.data,
});
export const useCreateGroup = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/groups', input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
    });
};
export const useUpdateGroup = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...input }) => (await api.put(`/groups/${id}`, input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
    });
};
export const useDeleteGroup = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`/groups/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
    });
};
// ---------- Available dates (menu calendar) ----------
export const useAvailableDates = (from, to) => useQuery({
    queryKey: ['available-dates', from, to],
    queryFn: async () => (await api.get('/menus/available-dates', { params: { from, to } })).data.data,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
});
// ---------- Order on-behalf-of helper ----------
export const useCreateOrderForUser = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/orders', {
            dailyMenuId: input.dailyMenuId, userId: input.userId,
            items: [{ foodId: input.foodId, quantity: 1 }],
        })).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    });
};
// ---------- Companies ----------
export const useCompanies = () => useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.get('/companies')).data.data,
});
export const useCreateCompany = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/companies', input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
    });
};
export const useUpdateCompany = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...input }) => (await api.put(`/companies/${id}`, input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
    });
};
// Per-company food prices (override the base price for one company).
export const useCompanyWithPrices = (companyId) => useQuery({
    queryKey: ['company', companyId, 'with-prices'],
    enabled: !!companyId,
    queryFn: async () => (await api.get(`/companies/${companyId}`)).data.data,
});
export const useSetCompanyFoodPrice = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/companies/food-prices', input)).data.data,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['company', vars.companyId, 'with-prices'] });
        },
    });
};
export const useDeleteCompanyFoodPrice = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.delete(`/companies/food-prices/${input.companyId}/${input.foodId}`)).data,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['company', vars.companyId, 'with-prices'] });
        },
    });
};
export const useDeleteCompany = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`/companies/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
    });
};
// ---------- Users ----------
export const useUsers = (page = 1, pageSize = 50) => useQuery({
    queryKey: ['users', page, pageSize],
    queryFn: async () => (await api.get('/users', { params: { page, pageSize } })).data,
});
export const useCreateUser = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/users', input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    });
};
export const useUpdateUser = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...input }) => (await api.put(`/users/${id}`, input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    });
};
export const useDeleteUser = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    });
};
export const useResetUserPassword = () => useMutation({
    mutationFn: async ({ id, newPassword }) => (await api.post(`/users/${id}/reset-password`, { newPassword })).data,
});
// ---------- Menus ----------
export const useMenuByDate = (jalali) => useQuery({
    queryKey: ['menu', jalali],
    enabled: !!jalali,
    queryFn: async () => (await api.get(`/menus/by-date/${jalali}`)).data.data,
});
export const useMenus = (from, to) => useQuery({
    queryKey: ['menus', from, to],
    queryFn: async () => (await api.get('/menus', { params: { from, to } })).data.data,
    refetchOnWindowFocus: true,
});
export const useUpsertMenu = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/menus', input)).data.data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['menu'] });
            qc.invalidateQueries({ queryKey: ['menus'] });
            qc.invalidateQueries({ queryKey: ['available-dates'] });
        },
    });
};
// ---------- Orders ----------
export const useMyOrders = (params = {}) => useQuery({
    queryKey: ['orders', 'mine', params],
    queryFn: async () => (await api.get('/orders', { params })).data,
});
export const useCreateOrder = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/orders', input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    });
};
export const useCancelOrder = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`/orders/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    });
};
// All orders (admin/super-user view). Same endpoint, role-scoped server-side.
export const useOrders = (params = {}) => useQuery({
    queryKey: ['orders', 'all', params],
    queryFn: async () => (await api.get('/orders', { params })).data,
});
export const useUpdateOrderStatus = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, status }) => (await api.put(`/orders/${id}/status`, { status })).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    });
};
// ---------- Reports ----------
export const useReportSummary = (params = {}) => useQuery({
    queryKey: ['report', 'summary', params],
    queryFn: async () => (await api.get('/reports/summary', { params })).data.data,
});
// ---------- Tables / Reservations ----------
export const useFloorPlan = (from, to) => useQuery({
    queryKey: ['floor-plan', from, to],
    queryFn: async () => (await api.get('/tables/floor-plan', { params: { from, to } })).data.data,
});
export const useUpdateTablePositions = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (tables) => (await api.post('/tables/positions', { tables })).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['floor-plan'] }),
    });
};
export const useReserveTable = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => (await api.post('/reservations', input)).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['floor-plan'] }),
    });
};
export const useInquiries = (status) => useQuery({
    queryKey: ['inquiries', status ?? 'all'],
    queryFn: async () => (await api.get('/inquiries', { params: status ? { status } : {} })).data,
    refetchOnWindowFocus: true,
});
export const useUpdateInquiry = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, status }) => (await api.put(`/inquiries/${id}`, { status })).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['inquiries'] }),
    });
};
export const useDeleteInquiry = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`/inquiries/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['inquiries'] }),
    });
};
export const useBackups = () => useQuery({
    queryKey: ['backups'],
    queryFn: async () => (await api.get('/backups')).data.data,
    refetchOnWindowFocus: true,
});
export const useCreateBackup = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => (await api.post('/backups')).data.data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['backups'] }),
    });
};
export const useDeleteBackup = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (fileName) => (await api.delete(`/backups/${fileName}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['backups'] }),
    });
};
export const useRestoreBackup = () => useMutation({
    mutationFn: async (fileName) => (await api.post(`/backups/${fileName}/restore`)).data,
});
// ---------- Birthdays ----------
export const useBirthdaysToday = () => useQuery({
    queryKey: ['birthdays', 'today'],
    queryFn: async () => (await api.get('/birthdays/today')).data.data,
    staleTime: 60 * 60_000,
});
export const useBirthdaysUpcoming = (days = 14) => useQuery({
    queryKey: ['birthdays', 'upcoming', days],
    queryFn: async () => (await api.get('/birthdays/upcoming', { params: { days } })).data.data,
    staleTime: 60 * 60_000,
});
